import { useQueryClient } from '@tanstack/react-query';
import useMutationApi from 'src/api/useMutationApi';
import { CompanyDTO } from './useCompany';

export interface UpdateCompanyData {
  name?: string;
  shortName?: string | null;
  email?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
  metadata?: Record<string, unknown>; // Admin-only field
}

export interface UpdateCompanyResponse {
  company: CompanyDTO;
}

const useUpdateCompany = (companyId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutationApi<UpdateCompanyData, UpdateCompanyResponse>({
    endpoint: `core/companies/${companyId}`,
    method: 'PUT',
    successMessage: 'Company updated successfully',
    errorMessage: 'Failed to update company',
    options: {
      onSuccess: () => {
        // Invalidate the specific company query
        void queryClient.invalidateQueries({
          queryKey: [`core/companies/${companyId}`],
        });

        // Invalidate all paginated companies queries to refresh the list
        void queryClient.invalidateQueries({
          queryKey: ['companies', 'paginated'],
        });

        // Call the optional user-provided onSuccess callback
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  });
};

export default useUpdateCompany;
